/**
 * The master token, shown once.
 *
 * Three acts mint one: approving a pending partner from the review queue,
 * creating one directly, and rotating an active one. All three return the
 * plaintext in the response body and never again, so all three end here.
 *
 * It does not auto-advance and it does not close itself. A queue is built for
 * clearing items quickly, which is exactly where a reveal-once value gets
 * dismissed by reflex.
 */
import type { FederatedPartnerWithSecret } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { Dialog } from '../../ui/Dialog.js';

export function PartnerTokenDialog({
  issued,
  onClose,
}: {
  issued: FederatedPartnerWithSecret | null;
  onClose: () => void;
}) {
  const toast = useToast();
  return (
    <Dialog
      open={issued !== null}
      onClose={onClose}
      icon="key"
      tone="warn"
      title={`Master token for ${issued?.name ?? ''}`}
      subtitle="Shown once. Copy it before closing — it cannot be retrieved again."
    >
      <p style={{ marginBottom: 16, fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        This is the only time this token is shown. Closing this dialog discards it permanently.
      </p>
      <div
        style={{
          marginBottom: 18,
          borderRadius: 'var(--radius-input)',
          border: '1px solid var(--warn)',
          borderStyle: 'dashed',
          background: 'var(--warn-tint)',
          padding: 11,
        }}
      >
        <Typography variant="eyebrow" style={{ marginBottom: 7, display: 'block' }}>
          Master token
        </Typography>
        <code
          style={{
            display: 'block',
            wordBreak: 'break-all',
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--warn)',
          }}
          className="mono"
        >
          {issued?.secret}
        </code>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
        <Button
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(issued?.secret ?? '');
            toast.success('Copied to clipboard');
          }}
        >
          Copy
        </Button>
        <Button variant="primary" onClick={onClose}>
          I have saved it
        </Button>
      </div>
    </Dialog>
  );
}
